import { cn } from '@/lib/utils';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** `sm` matches the compact config rows; `md` is the default control size. */
  size?: 'sm' | 'md';
  /** Required — a bare switch has no visible text of its own for a screen reader. */
  'aria-label': string;
}

/**
 * On/off switch.
 *
 * The knob is placed by flexbox and moved with translate-x, deliberately NOT by absolute
 * positioning. An absolutely positioned knob with no `left` falls back to its static
 * position, and a <button> centres its content by default — so the knob renders mid-pill
 * and the on/off states become indistinguishable.
 *
 * Both sizes use `border-2 border-transparent` rather than padding, so the knob travels
 * exactly the content-box width and sits flush at either end.
 */
export function Switch({
  checked,
  onChange,
  disabled,
  size = 'md',
  'aria-label': ariaLabel,
}: SwitchProps) {
  const sm = size === 'sm';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex shrink-0 cursor-pointer touch-manipulation items-center rounded-full',
        'border-2 border-transparent transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sm ? 'h-5 w-9' : 'h-6 w-11',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
    >
      <span
        className={cn(
          'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform',
          sm ? 'h-4 w-4' : 'h-5 w-5',
          checked ? (sm ? 'translate-x-4' : 'translate-x-5') : 'translate-x-0',
        )}
      />
    </button>
  );
}
